import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LibCoreLayoutModule } from '../../core-layout';
import { PipesModule } from '../../../pipes';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import {
  APP_MODE,
  BusinessObject,
  Customer,
  DEBOUNCE_TIME,
  DEFAULT_CUSTOMER_NAME,
} from '../../../models';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BaseComponent } from '../../../core';
import { DialogService } from '../../../services';
import {
  CustomerFormDialogComponent,
  CustomerFormDialogComponentData,
} from '../../customer-form-dialog';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
} from 'rxjs/operators';
import {
  BusinessObjectUtils,
  CustomerUtils,
  StringUtils,
} from '../../../utils';
import { CustomerNameFormatterPipe } from '../../../pipes/customer-name-formatter.pipe';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

export interface CustomerSelectionDialogComponentData {
  mode: APP_MODE;
  businessObject: BusinessObject;
  customers: Customer[];
  currentSelectedCustomerId?: number;
}

@Component({
  selector: 'lib-customer-selection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    PipesModule,
    TranslateModule,
    ReactiveFormsModule,
    // material design
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  templateUrl: './customer-selection-dialog.component.html',
  styleUrl: './customer-selection-dialog.component.scss',
})
export class CustomerSelectionDialogComponent extends BaseComponent {
  public searchKeywordControl = new FormControl<string>(null);

  public businessObject: BusinessObject;
  public filteredCustomersToDisplay: Customer[] = [];

  public scrollToTopTrigger = false;

  private mode: APP_MODE;
  private readonly currentSelectedCustomerId: number;
  private readonly customers: Customer[];
  private filteredCustomers: Customer[];

  private readonly INFINITIVE_SCROLL_BATCH_SIZE = 50;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: CustomerSelectionDialogComponentData,
    private dialogRef: MatDialogRef<CustomerSelectionDialogComponent>,
    private dialogService: DialogService,
    private customerNameFormatterPipe: CustomerNameFormatterPipe
  ) {
    super();

    this.businessObject = this.data?.businessObject;
    this.customers = this.data?.customers;
    this.currentSelectedCustomerId = this.data?.currentSelectedCustomerId;

    this.searchKeywordControl.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed),
        debounceTime(DEBOUNCE_TIME),
        distinctUntilChanged()
      )
      .subscribe((value) => {
        this.refreshFilteredCustomersToDisplay(value);
      });

    this.refreshFilteredCustomersToDisplay(this.searchKeywordControl.value);
  }

  public isCustomerSelected(customerId: number): boolean {
    return customerId === this.currentSelectedCustomerId;
  }

  public selectCustomer(customer: Customer) {
    this.dialogRef.close(customer);
  }

  public closeDialog(): void {
    this.dialogRef.close(null);
  }

  public openCustomerFormDialog(): void {
    const customerToEdit = CustomerUtils.generateCustomerFromKeyword(
      this.searchKeywordControl.value
    );

    const dialogRef = this.dialogService.openDialog(
      CustomerFormDialogComponent,
      this.dialogService.createDefaultDialogConfig({
        mode: this.mode,
        displayFullContactData:
          BusinessObjectUtils.shouldDisplayFullContactDataOfCustomer(
            this.businessObject,
            this.mode
          ),
        customer: customerToEdit,
        businessObject: this.businessObject,
      } as CustomerFormDialogComponentData)
    );

    dialogRef
      .afterClosed()
      .pipe(filter((customer) => customer))
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((customer) => {
        if (customer?.id) {
          this.dialogRef.close(customer);
        }
      });
  }

  public addMoreFilteredCustomers(): void {
    if (
      this.filteredCustomersToDisplay?.length >= this.filteredCustomers?.length
    ) {
      return;
    }

    this.filteredCustomersToDisplay = [
      ...this.filteredCustomersToDisplay,
      ...this.filteredCustomers.slice(
        this.filteredCustomersToDisplay.length,
        this.filteredCustomersToDisplay.length +
          this.INFINITIVE_SCROLL_BATCH_SIZE
      ),
    ];
  }

  private refreshFilteredCustomersToDisplay(keyword: string): void {
    this.filteredCustomers = this.filterCustomersByKeyword(keyword) || [];
    this.filteredCustomersToDisplay =
      this.filteredCustomers.slice(0, this.INFINITIVE_SCROLL_BATCH_SIZE) || [];
    this.scrollToTopTrigger = !this.scrollToTopTrigger;
  }

  private filterCustomersByKeyword(originalKeyword: string): Customer[] {
    const keyword = StringUtils.simplifyString(originalKeyword);

    if (!keyword?.length) {
      return this.customers;
    }

    let keywordAsNumber;
    try {
      keywordAsNumber = Number(keyword);
    } catch (_e) {}

    return this.customers.filter((customer) => {
      const phoneNr = StringUtils.simplifyString(customer.phoneNr);
      if (phoneNr?.includes(keyword) || phoneNr.includes(keywordAsNumber)) {
        return true;
      }

      const email = StringUtils.simplifyString(customer.email);
      if (email?.includes(keyword)) {
        return true;
      }

      const fullName = StringUtils.simplifyString(
        this.customerNameFormatterPipe.transform(customer)
      );
      return fullName.includes(keyword);
    });
  }
}
